import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../config/axiosInterceptor";

const FamilyRankRewards = () => {
  const baseUrl = process.env.REACT_APP_BASEURL1;
  const [rewards, setRewards] = useState([]);
  const [tags, setTags] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [globalBanner, setGlobalBanner] = useState("");
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState("");
  const [bannerUploading, setBannerUploading] = useState(false);
  const [formData, setFormData] = useState({
    rank: "",
    minPoints: "",
    mCoins: "",
    tags: [],
  });

  useEffect(() => {
    console.log("FamilyRankRewards component mounted");
    fetchRewards();
    fetchTags();
    fetchGlobalBanner();
  }, []);

  useEffect(() => {
    console.log("Modal state changed:", isModalOpen);
  }, [isModalOpen]);

  const fetchGlobalBanner = async () => {
    try {
      const { data } = await axiosInstance.get(`${baseUrl}/admin/familyrankreward/banner`);
      if (data.success === 1) setGlobalBanner(data.data.banner || "");
    } catch (_) {}
  };

  const uploadGlobalBanner = async () => {
    if (!bannerFile) return toast.error("Please select a banner image");
    setBannerUploading(true);
    try {
      const fd = new FormData();
      fd.append("banner", bannerFile);
      const { data } = await axiosInstance.post(
        `${baseUrl}/admin/familyrankreward/banner`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (data.success === 1) {
        toast.success("Banner updated successfully");
        setGlobalBanner(data.data.banner);
        setBannerFile(null);
        setBannerPreview("");
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBannerUploading(false);
    }
  };

  const fetchRewards = async () => {
    try {
      const { data } = await axiosInstance.get(
        `${baseUrl}/admin/familyrankreward/getall`
      );
      console.log("Fetch rewards response:", data);
      if (data.success === 1) {
        setRewards(data.data);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Error fetching rewards:", error);
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const fetchTags = async () => {
    try {
      const { data } = await axiosInstance.get(`${baseUrl}/admin/tag/getTag`);
      console.log("Fetch tags response:", data);
      if (data.success === 1) {
        setTags(data.tags || data.data || []);
      } else {
        console.log("Tags fetch failed, data:", data);
      }
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const openModal = (reward = null) => {
    console.log("Opening modal, reward:", reward);
    if (reward) {
      setIsEdit(true);
      setFormData({
        rank: reward.rank,
        minPoints: reward.minPoints,
        mCoins: reward.mCoins,
        tags: reward.tags || [],
      });
    } else {
      setIsEdit(false);
      setFormData({
        rank: "",
        minPoints: "",
        mCoins: "",
        tags: [],
      });
    }
    setIsModalOpen(true);
    console.log("Modal state set to true");
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      rank: "",
      minPoints: "",
      mCoins: "",
      tags: [],
    });
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addTagReward = () => {
    setFormData((prev) => ({
      ...prev,
      tags: [...prev.tags, { tagId: "", amount: 1 }],
    }));
  };

  const removeTagReward = (index) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index),
    }));
  };

  const handleTagChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.map((tag, i) =>
        i === index ? { ...tag, [field]: value } : tag
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.rank || !formData.minPoints || !formData.mCoins) {
      toast.error("Please fill all required fields");
      return;
    }

    // Validate tags
    const validTags = formData.tags.filter(tag => tag.tagId && tag.amount > 0);

    try {
      const payload = {
        rank: parseInt(formData.rank),
        minPoints: parseInt(formData.minPoints),
        mCoins: parseInt(formData.mCoins),
        tags: validTags,
      };

      console.log("Submitting payload:", payload);

      const { data } = await axiosInstance.post(
        `${baseUrl}/admin/familyrankreward/create`,
        payload
      );

      console.log("Response:", data);

      if (data.success === 1) {
        toast.success(data.message);
        fetchRewards();
        closeModal();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const handleDelete = async (rank) => {
    if (window.confirm(`Are you sure you want to delete rank ${rank} reward?`)) {
      try {
        const { data } = await axiosInstance.delete(
          `${baseUrl}/admin/familyrankreward/delete/${rank}`
        );

        if (data.success === 1) {
          toast.success(data.message);
          fetchRewards();
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  return (
    <>
      <section className="content-header">
        <h1>Family Rank Rewards</h1>
        <ol className="breadcrumb">
          <li>
            <Link to="/admin">
              <i className="fa fa-dashboard"></i> Home
            </Link>
          </li>
          <li className="active">Family Rank Rewards</li>
        </ol>
      </section>

      <section className="content">
        <div className="row">
          <div className="col-xs-12">
            {/* Global Banner Card */}
            <div className="box">
              <div className="box-header">
                <h3 className="box-title">Rank Rewards Banner</h3>
              </div>
              <div className="box-body">
                <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <label>Current Banner</label>
                    <div style={{ marginTop: 6 }}>
                      {globalBanner ? (
                        <img
                          src={baseUrl + globalBanner}
                          alt="rank reward banner"
                          style={{ maxHeight: 80, borderRadius: 4, border: "1px solid #ddd" }}
                        />
                      ) : (
                        <span className="label label-default">No banner set</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label>Upload New Banner</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleBannerChange}
                      style={{ marginTop: 6 }}
                    />
                    {bannerPreview && (
                      <img
                        src={bannerPreview}
                        alt="preview"
                        style={{ marginTop: 6, maxHeight: 60, borderRadius: 4 }}
                      />
                    )}
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={uploadGlobalBanner}
                    disabled={bannerUploading || !bannerFile}
                  >
                    {bannerUploading ? "Uploading..." : "Save Banner"}
                  </button>
                </div>
              </div>
            </div>

            <div className="box">
              <div className="box-header">
                <h3 className="box-title">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal();
                    }}
                    className="btn btn-success btn-sm"
                  >
                    <i className="fa fa-plus"></i> Add Rank Reward
                  </button>
                </h3>
              </div>

              <div className="box-body">
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Min Points</th>
                      <th>M-Coins</th>
                      <th>Tags</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rewards.map((reward) => (
                      <tr key={reward._id}>
                        <td>
                          <strong>Rank #{reward.rank}</strong>
                        </td>
                        <td>{reward.minPoints.toLocaleString()}</td>
                        <td>{reward.mCoins.toLocaleString()}</td>
                        <td>
                          {reward.tags && reward.tags.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                              {reward.tags.map((tag, idx) => (
                                <li key={idx}>
                                  {tag.tagId?.title || "N/A"} x {tag.amount}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            "No tags"
                          )}
                        </td>
                        <td>
                          <button
                            onClick={() => openModal(reward)}
                            className="btn btn-warning btn-xs"
                            style={{ marginRight: 5 }}
                          >
                            <i className="fa fa-edit"></i> Edit
                          </button>
                          <button
                            onClick={() => handleDelete(reward.rank)}
                            className="btn btn-danger btn-xs"
                          >
                            <i className="fa fa-trash"></i> Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {isModalOpen && (
        <div
          style={{ 
            display: "block", 
            backgroundColor: "rgba(0,0,0,0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            overflow: "auto",
            padding: "20px"
          }}
          onClick={(e) => {
            // Only close if clicking directly on the backdrop
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div 
            style={{ 
              maxWidth: "600px",
              margin: "50px auto",
              backgroundColor: "white",
              borderRadius: "4px",
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)"
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              padding: "15px",
              borderBottom: "1px solid #e5e5e5"
            }}>
              <button
                type="button"
                style={{
                  float: "right",
                  fontSize: "21px",
                  fontWeight: "bold",
                  lineHeight: "1",
                  color: "#000",
                  opacity: "0.2",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer"
                }}
                onClick={closeModal}
              >
                <span>&times;</span>
              </button>
              <h4 style={{ margin: 0 }}>
                {isEdit ? "Edit" : "Add"} Rank Reward
              </h4>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ padding: "15px" }}>
                <div className="form-group">
                  <label>
                    Rank <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="rank"
                    value={formData.rank}
                    onChange={handleInputChange}
                    placeholder="Enter rank (1-10)"
                    min="1"
                    max="10"
                    required
                  />
                </div>

                  <div className="form-group">
                    <label>
                      Minimum Points <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="minPoints"
                      value={formData.minPoints}
                      onChange={handleInputChange}
                      placeholder="Enter minimum points"
                      min="0"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      M-Coins <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="mCoins"
                      value={formData.mCoins}
                      onChange={handleInputChange}
                      placeholder="Enter M-Coins reward"
                      min="0"
                      required
                    />
                  </div>

                <div className="form-group">
                  <label>Tag Rewards</label>
                    <button
                      type="button"
                      className="btn btn-info btn-xs"
                      onClick={addTagReward}
                      style={{ marginLeft: 10 }}
                    >
                      <i className="fa fa-plus"></i> Add Tag
                    </button>

                    {formData.tags.map((tag, index) => (
                      <div
                        key={index}
                        className="row"
                        style={{ marginTop: 10 }}
                      >
                        <div className="col-md-6">
                          <select
                            className="form-control"
                            value={tag.tagId}
                            onChange={(e) =>
                              handleTagChange(index, "tagId", e.target.value)
                            }
                          >
                            <option value="">Select Tag</option>
                            {tags.map((t) => (
                              <option key={t._id} value={t._id}>
                                {t.title}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-4">
                          <input
                            type="number"
                            className="form-control"
                            value={tag.amount}
                            onChange={(e) =>
                              handleTagChange(
                                index,
                                "amount",
                                parseInt(e.target.value) || 1
                              )
                            }
                            placeholder="Amount"
                            min="1"
                          />
                        </div>
                        <div className="col-md-2">
                          <button
                            type="button"
                            className="btn btn-danger btn-xs"
                            onClick={() => removeTagReward(index)}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              <div style={{ 
                padding: "15px",
                borderTop: "1px solid #e5e5e5",
                textAlign: "right"
              }}>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={closeModal}
                  style={{ marginRight: "10px" }}
                >
                  Close
                </button>
                <button type="submit" className="btn btn-primary">
                  {isEdit ? "Update" : "Create"} Reward
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FamilyRankRewards;
